import { useEffect, useState, useCallback, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import DashboardLayout from '@/components/layout/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Checkbox } from '@/components/ui/checkbox';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { CalendarDays, Users, UserPlus, Trash2, EyeOff, Eye, ClipboardList, HeartHandshake } from 'lucide-react';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import AssistantsRegistryPanel from './AssistantsRegistryPanel';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LS_KEY = 'staff_register.selected_ids';

// Attendance status options (order also controls the dropdown listing)
const STATUS_OPTIONS = [
  { value: '',      label: '—' },
  { value: 'ON',    label: 'On Duty' },
  { value: 'OFF',   label: 'Off Duty' },
  { value: 'LEAVE', label: 'Leave' },
  { value: 'OT',    label: 'Overtime' },
  { value: 'SAT',   label: 'Saturday' },
  { value: 'PH',    label: 'Public Holiday' },
];

const CELL_STYLE = {
  '': 'bg-white text-slate-400 hover:bg-slate-50',
  ON: 'bg-emerald-100 text-emerald-800 font-bold hover:bg-emerald-200',
  OFF: 'bg-rose-100 text-rose-800 font-bold hover:bg-rose-200',
  LEAVE: 'bg-violet-100 text-violet-800 font-bold hover:bg-violet-200',
  OT: 'bg-blue-100 text-blue-800 font-bold hover:bg-blue-200',
  SAT: 'bg-orange-100 text-orange-800 font-bold hover:bg-orange-200',
  PH: 'bg-fuchsia-100 text-fuchsia-800 font-bold hover:bg-fuchsia-200',
};

const WEEKEND_LABELS = new Set(['Sat', 'Sun']);

export default function StaffRegisterPage() {
  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  const today = new Date().toISOString().slice(0, 7);
  const [month, setMonth] = useState(today);
  const [days, setDays] = useState([]);
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(false);
  const [savingCell, setSavingCell] = useState('');

  // Selected staff IDs (persisted in localStorage) — empty set = "show all"
  const [selectedIds, setSelectedIds] = useState(() => {
    try {
      const raw = localStorage.getItem(LS_KEY);
      return new Set(raw ? JSON.parse(raw) : []);
    } catch { return new Set(); }
  });

  const persistSelection = (idSet) => {
    setSelectedIds(new Set(idSet));
    try { localStorage.setItem(LS_KEY, JSON.stringify([...idSet])); } catch { /* no-op */ }
  };

  const toggleStaff = (id) => {
    const s = new Set(selectedIds);
    if (s.has(id)) s.delete(id); else s.add(id);
    persistSelection(s);
  };
  const selectAll = () => persistSelection(new Set(staff.map(s => s.staff_id)));
  const clearSelection = () => persistSelection(new Set());

  // Add-staff dialog
  const [addOpen, setAddOpen] = useState(false);
  const [addForm, setAddForm] = useState({ full_name: '', role: 'internal_staff', position: '', hire_date: '', default_basic_salary: 0, contact_number: '' });
  const [addSaving, setAddSaving] = useState(false);
  const [showHidden, setShowHidden] = useState(false);

  const addStaff = async () => {
    if (!addForm.full_name.trim()) return toast.error('Name required');
    setAddSaving(true);
    try {
      await axios.post(`${API_URL}/staff-register/staff`, {
        full_name: addForm.full_name.trim(),
        role: addForm.role,
        position: addForm.position.trim(),
        hire_date: addForm.hire_date || null,
        default_basic_salary: parseFloat(addForm.default_basic_salary) || 0,
        contact_number: addForm.contact_number.trim(),
      }, { headers });
      toast.success(`${addForm.full_name} added to register`);
      setAddOpen(false);
      setAddForm({ full_name: '', role: 'internal_staff', position: '', hire_date: '', default_basic_salary: 0, contact_number: '' });
      await fetchGrid();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Add failed');
    } finally { setAddSaving(false); }
  };

  const removeStaff = async (s) => {
    if (!window.confirm(`Remove ${s.full_name} from the register?\n\nTheir attendance history and payslips will be preserved. You can reactivate them later.`)) return;
    try {
      await axios.delete(`${API_URL}/staff-register/staff/${s.staff_id}`, { headers });
      toast.success(`${s.full_name} removed`);
      await fetchGrid();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Remove failed');
    }
  };

  const toggleHidden = async (s) => {
    const action = s.hidden ? 'unhide' : 'hide';
    try {
      await axios.post(`${API_URL}/staff-register/staff/${s.staff_id}/${action}`, {}, { headers });
      toast.success(`${s.full_name} ${s.hidden ? 'restored' : 'hidden'}`);
      await fetchGrid();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed');
    }
  };

  const fetchGrid = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/staff-register`, { headers, params: { month, include_hidden: showHidden } });
      setDays(res.data?.days || []);
      setStaff(res.data?.staff || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load register');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [month, showHidden]);

  useEffect(() => { fetchGrid(); }, [fetchGrid]);

  const setCell = async (staffId, day, next) => {
    const staffIndex = staff.findIndex(r => r.staff_id === staffId);
    if (staffIndex < 0) return;
    const row = staff[staffIndex];
    const key = `${row.staff_id}-${day}`;
    setSavingCell(key);

    // Optimistic update
    const newStaff = staff.slice();
    const newEntries = { ...row.entries, [day]: next };
    newStaff[staffIndex] = { ...row, entries: newEntries };
    setStaff(newStaff);

    try {
      const res = await axios.post(
        `${API_URL}/staff-register/cell`,
        { staff_id: row.staff_id, month, day, value: next },
        { headers }
      );
      // Sync totals from server response
      newStaff[staffIndex] = { ...newStaff[staffIndex], totals: res.data?.totals || row.totals };
      setStaff(newStaff);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Save failed');
      // Revert
      const revert = staff.slice();
      revert[staffIndex] = row;
      setStaff(revert);
    } finally {
      setSavingCell('');
    }
  };

  const totalDays = days.length;
  const visibleStaff = useMemo(
    () => (selectedIds.size === 0 ? staff : staff.filter(s => selectedIds.has(s.staff_id))),
    [staff, selectedIds],
  );
  const grandWorked = visibleStaff.reduce((s, r) => s + (r.totals?.worked || 0), 0);
  const selectionLabel = selectedIds.size === 0
    ? `All staff (${staff.length})`
    : `${selectedIds.size} of ${staff.length} staff`;

  return (
    <DashboardLayout>
      <div className="space-y-4">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <CalendarDays className="h-6 w-6" /> Internal Staff Register
            </h1>
            <p className="text-sm text-muted-foreground">
              Pick a status from each cell&apos;s <b>dropdown</b>: <b className="text-emerald-700">ON</b> (On Duty) · <b className="text-rose-700">OFF</b> (Off Duty) · <b className="text-violet-700">LEAVE</b> · <b className="text-blue-700">OT</b> (Overtime) · <b className="text-orange-700">SAT</b> (Saturday) · <b className="text-fuchsia-700">PH</b> (Public Holiday).
              Days Worked = ON + OT + SAT + PH. The total auto-feeds the payslip "Days Worked" field.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              className={`h-10 gap-2 ${showHidden ? 'bg-slate-100 border-slate-400' : ''}`}
              onClick={() => setShowHidden((v) => !v)}
              data-testid="show-hidden-btn"
              title={showHidden ? 'Hiding hidden staff' : 'Show hidden staff'}
            >
              {showHidden ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}
              {showHidden ? 'Showing Hidden' : 'Show Hidden'}
            </Button>
            <Button
              variant="outline"
              className="h-10 gap-2 border-emerald-300 text-emerald-700 hover:bg-emerald-50"
              onClick={() => setAddOpen(true)}
              data-testid="add-staff-btn"
            >
              <UserPlus className="h-4 w-4" /> Add Staff
            </Button>
            <Popover>
              <PopoverTrigger asChild>
                <Button variant="outline" className="h-10 gap-2" data-testid="staff-picker-btn">
                  <Users className="h-4 w-4" />
                  {selectionLabel}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-72 p-3" align="end">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs font-semibold">Show staff</span>
                  <div className="flex gap-1">
                    <Button size="sm" variant="ghost" className="h-6 px-2 text-xs" onClick={selectAll} data-testid="staff-picker-all">All</Button>
                    <Button size="sm" variant="ghost" className="h-6 px-2 text-xs" onClick={clearSelection} data-testid="staff-picker-clear">Clear</Button>
                  </div>
                </div>
                <div className="max-h-72 overflow-y-auto space-y-1">
                  {staff.length === 0 ? (
                    <p className="text-xs text-muted-foreground py-2">No internal staff yet.</p>
                  ) : staff.map((s) => {
                    const checked = selectedIds.size === 0 || selectedIds.has(s.staff_id);
                    return (
                      <label key={s.staff_id} className="flex items-center gap-2 text-sm py-1 cursor-pointer hover:bg-slate-50 rounded px-1">
                        <Checkbox
                          checked={checked}
                          onCheckedChange={() => {
                            // First interaction when 'show all' is implicit: switch to explicit selection
                            if (selectedIds.size === 0) {
                              const all = new Set(staff.map(x => x.staff_id));
                              all.delete(s.staff_id);
                              persistSelection(all);
                            } else {
                              toggleStaff(s.staff_id);
                            }
                          }}
                          data-testid={`staff-pick-${s.staff_id}`}
                        />
                        <span>{s.full_name}</span>
                      </label>
                    );
                  })}
                </div>
                <p className="text-[10px] text-muted-foreground mt-2">Clear = show all. Selection is remembered on this device.</p>
              </PopoverContent>
            </Popover>
            <Input
              type="month"
              value={month}
              onChange={(e) => setMonth(e.target.value)}
              className="w-44"
              data-testid="register-month"
            />
          </div>
        </div>

        <Tabs defaultValue="register" className="w-full">
          <TabsList className="grid grid-cols-2 w-full max-w-md" data-testid="staff-register-tabs">
            <TabsTrigger value="register" className="gap-2" data-testid="tab-register">
              <ClipboardList className="h-4 w-4" /> Register
            </TabsTrigger>
            <TabsTrigger value="assistants" className="gap-2" data-testid="tab-assistants">
              <HeartHandshake className="h-4 w-4" /> Assistants
            </TabsTrigger>
          </TabsList>

          <TabsContent value="assistants" className="pt-3">
            <AssistantsRegistryPanel />
          </TabsContent>

          <TabsContent value="register" className="pt-3">

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center justify-between">
              <span>{month} · {totalDays} days · {visibleStaff.length} of {staff.length} staff shown · Total worked-days: <b className="text-emerald-700">{grandWorked}</b></span>
              <span className="flex items-center gap-2 text-xs font-normal flex-wrap justify-end">
                <span className="inline-flex items-center gap-1"><span className="inline-block w-3 h-3 rounded-sm bg-emerald-200 border border-emerald-400" /> ON</span>
                <span className="inline-flex items-center gap-1"><span className="inline-block w-3 h-3 rounded-sm bg-rose-200 border border-rose-400" /> OFF</span>
                <span className="inline-flex items-center gap-1"><span className="inline-block w-3 h-3 rounded-sm bg-violet-200 border border-violet-400" /> LEAVE</span>
                <span className="inline-flex items-center gap-1"><span className="inline-block w-3 h-3 rounded-sm bg-emerald-100 border border-emerald-400" /> <b className="text-emerald-700">P</b>=Paid</span>
                <span className="inline-flex items-center gap-1"><span className="inline-block w-3 h-3 rounded-sm bg-rose-100 border border-rose-400" /> <b className="text-rose-700">U</b>=Unpaid</span>
                <span className="inline-flex items-center gap-1"><span className="inline-block w-3 h-3 rounded-sm bg-blue-200 border border-blue-400" /> OT</span>
                <span className="inline-flex items-center gap-1"><span className="inline-block w-3 h-3 rounded-sm bg-orange-200 border border-orange-400" /> SAT</span>
                <span className="inline-flex items-center gap-1"><span className="inline-block w-3 h-3 rounded-sm bg-fuchsia-200 border border-fuchsia-400" /> PH</span>
              </span>
            </CardTitle>
          </CardHeader>
          <CardContent className="overflow-x-auto">
            {loading ? (
              <div className="text-center py-10 text-muted-foreground">Loading…</div>
            ) : visibleStaff.length === 0 ? (
              <div className="text-center py-10 text-muted-foreground">
                {staff.length === 0 ? (
                  <>No internal staff found. Create staff under <b>Internal Staff & Payslips</b> first.</>
                ) : (
                  <>No staff selected. Click <b>{selectionLabel}</b> above and pick at least one (or hit <b>All</b>).</>
                )}
              </div>
            ) : (
              <table className="w-full text-xs border-collapse" data-testid="register-grid">
                <thead>
                  <tr>
                    <th className="text-left p-1 sticky left-0 bg-white z-10 border-b border-r" style={{ minWidth: 70 }}>Date</th>
                    <th className="text-left p-1 sticky bg-white border-b border-r" style={{ left: 70, minWidth: 60, zIndex: 10 }}>Day</th>
                    {visibleStaff.map((s) => (
                      <th key={s.staff_id} className={`p-1 border-b border-r text-center font-semibold group ${s.hidden ? 'bg-slate-100 text-slate-500 italic' : ''}`} style={{ minWidth: 90 }}>
                        <div className="flex items-center justify-center gap-1">
                          <span>{s.full_name}{s.hidden ? ' (hidden)' : ''}</span>
                          <button
                            type="button"
                            className="opacity-0 group-hover:opacity-100 transition-opacity text-slate-500 hover:text-slate-800"
                            title={s.hidden ? `Unhide ${s.full_name}` : `Hide ${s.full_name} from register`}
                            onClick={() => toggleHidden(s)}
                            data-testid={`toggle-hidden-${s.staff_id}`}
                          >
                            {s.hidden ? <Eye className="h-3 w-3" /> : <EyeOff className="h-3 w-3" />}
                          </button>
                          <button
                            type="button"
                            className="opacity-0 group-hover:opacity-100 transition-opacity text-rose-500 hover:text-rose-700"
                            title={`Remove ${s.full_name} from register`}
                            onClick={() => removeStaff(s)}
                            data-testid={`remove-staff-${s.staff_id}`}
                          >
                            <Trash2 className="h-3 w-3" />
                          </button>
                        </div>
                        {s.assigned_vehicle_registration && (
                          <div
                            className="mt-0.5 inline-block px-1 py-0 rounded text-[9px] font-mono font-semibold bg-indigo-100 text-indigo-800 border border-indigo-200"
                            data-testid={`register-vehicle-${s.staff_id}`}
                            title="Assigned vehicle"
                          >
                            🚚 {s.assigned_vehicle_registration}
                          </div>
                        )}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {days.map((d) => {
                    const isWeekend = WEEKEND_LABELS.has(d.label);
                    return (
                      <tr key={d.day} className={isWeekend ? 'bg-slate-50' : ''}>
                        <td className={`p-1 border-b border-r font-mono sticky left-0 z-10 ${isWeekend ? 'bg-slate-50' : 'bg-white'}`}>{parseInt(d.day, 10)}</td>
                        <td className={`p-1 border-b border-r sticky z-10 ${isWeekend ? 'bg-slate-50 text-slate-500' : 'bg-white'}`} style={{ left: 70 }}>{d.label}</td>
                        {visibleStaff.map((s) => {
                          const v = s.entries[d.day] || '';
                          const key = `${s.staff_id}-${d.day}`;
                          const leaveStatus = v === 'LEAVE' ? (s.leave_status?.[d.day] || 'unpaid') : null;
                          const leaveTint = leaveStatus === 'paid'
                            ? 'bg-emerald-100 text-emerald-800 font-bold hover:bg-emerald-200'
                            : leaveStatus === 'unpaid'
                              ? 'bg-rose-100 text-rose-800 font-bold hover:bg-rose-200'
                              : null;
                          return (
                            <td key={key} className="p-0 border-b border-r text-center relative">
                              <select
                                value={v}
                                onChange={(e) => setCell(s.staff_id, d.day, e.target.value)}
                                disabled={savingCell === key}
                                className={`w-full h-7 text-[10px] tracking-wide text-center border-0 focus:outline-none focus:ring-1 focus:ring-slate-400 cursor-pointer transition-colors ${leaveTint || CELL_STYLE[v] || 'bg-white text-slate-400 hover:bg-slate-100'} ${savingCell === key ? 'opacity-50' : ''}`}
                                data-testid={`cell-${s.staff_id}-${d.day}`}
                                title={v === 'LEAVE'
                                  ? `${s.full_name} · ${d.day} ${d.label} · ${leaveStatus === 'paid' ? 'Paid Leave' : 'Unpaid Leave'}`
                                  : `${s.full_name} · ${d.day} ${d.label}`}
                              >
                                {STATUS_OPTIONS.map(opt => (
                                  <option key={opt.value || 'blank'} value={opt.value}>
                                    {opt.value ? `${opt.value} · ${opt.label}` : '—'}
                                  </option>
                                ))}
                              </select>
                              {leaveStatus && (
                                <span
                                  className={`pointer-events-none absolute top-0 right-0 text-[8px] leading-none font-bold rounded-bl px-1 py-[1px] ${leaveStatus === 'paid' ? 'bg-emerald-600 text-white' : 'bg-rose-600 text-white'}`}
                                  data-testid={`leave-badge-${s.staff_id}-${d.day}`}
                                >
                                  {leaveStatus === 'paid' ? 'P' : 'U'}
                                </span>
                              )}
                            </td>
                          );
                        })}
                      </tr>
                    );
                  })}
                  <tr className="bg-violet-50 font-semibold">
                    <td className="p-1 border-t" colSpan={2}>WORKED (ON+OT+SAT+PH)</td>
                    {visibleStaff.map((s) => (
                      <td key={`worked-${s.staff_id}`} className="p-1 border-t text-center text-violet-900" data-testid={`worked-${s.staff_id}`}>
                        {s.totals?.worked || 0}
                      </td>
                    ))}
                  </tr>
                  <tr className="text-[10px] text-muted-foreground">
                    <td className="p-1" colSpan={2}>ON · OFF · LEAVE (P/U) · OT · SAT · PH</td>
                    {visibleStaff.map((s) => (
                      <td key={`detail-${s.staff_id}`} className="p-1 text-center">
                        <span className="text-emerald-700">{s.totals?.on || 0}</span>
                        {' · '}<span className="text-rose-700">{s.totals?.off || 0}</span>
                        {' · '}<span title="Leave (Paid / Unpaid)"><span className="text-violet-700">{s.totals?.leave || 0}</span>
                          <span className="text-[9px] ml-0.5">(<span className="text-emerald-700">{s.totals?.leave_paid || 0}</span>/<span className="text-rose-700">{s.totals?.leave_unpaid || 0}</span>)</span></span>
                        {' · '}<span className="text-blue-700">{s.totals?.ot || 0}</span>
                        {' · '}<span className="text-orange-700">{s.totals?.sat || 0}</span>
                        {' · '}<span className="text-fuchsia-700">{s.totals?.ph || 0}</span>
                      </td>
                    ))}
                  </tr>
                </tbody>
              </table>
            )}
          </CardContent>
        </Card>

        <p className="text-xs text-muted-foreground">
          Tip: Open <b>Internal Staff & Payslips</b>, edit a staff member's payslip for this month, and use the <b>Pull from Register</b> button to auto-populate Days Worked.
        </p>
          </TabsContent>
        </Tabs>
      </div>

      {/* Add Staff dialog */}
      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent data-testid="add-staff-dialog">
          <DialogHeader>
            <DialogTitle>Add Staff to Register</DialogTitle>
            <DialogDescription>Creates a lightweight staff record — login credentials can be set later under Internal Staff & Payslips.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="text-sm font-medium">Full Name *</label>
              <Input value={addForm.full_name} onChange={(e) => setAddForm({ ...addForm, full_name: e.target.value })} placeholder="John Smith" data-testid="add-staff-name" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-sm font-medium">Role</label>
                <select value={addForm.role} onChange={(e) => setAddForm({ ...addForm, role: e.target.value })} className="h-9 w-full rounded border px-2 text-sm" data-testid="add-staff-role">
                  <option value="internal_staff">Internal Staff</option>
                  <option value="driver">Driver</option>
                </select>
              </div>
              <div>
                <label className="text-sm font-medium">Position</label>
                <Input value={addForm.position} onChange={(e) => setAddForm({ ...addForm, position: e.target.value })} placeholder="e.g. Delivery Driver" data-testid="add-staff-position" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-sm font-medium">Hire Date</label>
                <Input type="date" value={addForm.hire_date} onChange={(e) => setAddForm({ ...addForm, hire_date: e.target.value })} data-testid="add-staff-hire-date" />
              </div>
              <div>
                <label className="text-sm font-medium">Monthly Basic Salary</label>
                <Input type="number" min="0" step="0.01" value={addForm.default_basic_salary} onChange={(e) => setAddForm({ ...addForm, default_basic_salary: e.target.value })} data-testid="add-staff-salary" />
              </div>
            </div>
            <div>
              <label className="text-sm font-medium">Contact Number</label>
              <Input value={addForm.contact_number} onChange={(e) => setAddForm({ ...addForm, contact_number: e.target.value })} data-testid="add-staff-contact" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddOpen(false)}>Cancel</Button>
            <Button onClick={addStaff} disabled={addSaving} className="bg-emerald-600 hover:bg-emerald-700" data-testid="add-staff-submit">
              {addSaving ? 'Adding…' : 'Add Staff'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}
